import { useEffect } from 'react'
import { useStableHandler } from '@sketch/utils'

import { useGetAnnotationContext } from './useAnnotationOverlay'
import useHideAnnotationDots from './useHideAnnotationDots'

const IGNORED_ELEMENTS = ['INPUT', 'TEXTAREA']

const isEventTargetInput = (event: Event) =>
  event.target instanceof HTMLElement &&
  IGNORED_ELEMENTS.includes(event.target.nodeName)

interface UseAnnotationShortcutsParams {
  disabled?: boolean
}

const useAnnotationShortcuts = ({ disabled }: UseAnnotationShortcutsParams) => {
  const annotationContext = useGetAnnotationContext()
  const [hideAnnotations, setHideAnnotations] = useHideAnnotationDots() || []

  const handleAnnotationsVisibility = useStableHandler(() => {
    setHideAnnotations?.(!hideAnnotations)
  })

  const handlePlaceAnnotation = useStableHandler(() => {
    if (annotationContext.placeDraftAnnotation) {
      return
    }

    annotationContext.setPlaceDraftAnnotation?.(true)
  })

  const handleDismissAnnotationPopover = useStableHandler(() => {
    if (annotationContext.placeDraftAnnotation) {
      annotationContext.setPlaceDraftAnnotation?.(false)
    }
    if (annotationContext.activeAnnotation) {
      annotationContext?.setActiveAnnotation?.()
    }
  })

  useEffect(() => {
    if (annotationContext.status !== 'available' || disabled) {
      return
    }

    const handleAnnotationKeyEvents = (event: KeyboardEvent) => {
      if (!isEventTargetInput(event) && event.shiftKey && event.key === 'N') {
        /**
         * Allow the user to toggle the visibility of the annotations
         */
        handleAnnotationsVisibility()
      } else if (!isEventTargetInput(event) && event.key === 'n') {
        /**
         * Allow the user to place a draft annotation
         */
        handlePlaceAnnotation()
      } else if (event.key === 'Escape') {
        /*
         * Allow the user to dismiss the draft annotation
         * mode by key pressing "Escape"
         */
        handleDismissAnnotationPopover()
      }
    }

    window.addEventListener('keydown', handleAnnotationKeyEvents)

    return () => {
      window.removeEventListener('keydown', handleAnnotationKeyEvents)
    }
  }, [
    annotationContext.status,
    disabled,
    handleDismissAnnotationPopover,
    handleAnnotationsVisibility,
    handlePlaceAnnotation,
  ])
}

export default useAnnotationShortcuts
